/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import PropTypes from "prop-types";
import { EuiTabbedContent } from '../../../tabs';
import { EuiText } from '../../../text';
import { EuiButton } from '../../../button';
import { EuiAbsoluteTab } from './absolute_tab';
import { EuiRelativeTab } from './relative_tab';
import { getDateMode, DATE_MODES, toAbsoluteString, toRelativeString } from '../date_modes';
export var EuiDatePopoverContent = ({
  value,
  roundUp = false,
  onChange,
  dateFormat,
  timeFormat,
  locale,
  position,
  utcOffset
}) => {
  const onTabClick = selectedTab => {
    switch (selectedTab.id) {
      case DATE_MODES.ABSOLUTE:
        onChange(toAbsoluteString(value, roundUp));
        break;

      case DATE_MODES.RELATIVE:
        onChange(toRelativeString(value));
        break;
    }
  };

  const ariaLabel = `${position === 'start' ? 'Start' : 'End'} date:`;
  const renderTabs = [{
    id: DATE_MODES.ABSOLUTE,
    name: 'Absolute',
    content: <EuiAbsoluteTab dateFormat={dateFormat} timeFormat={timeFormat} locale={locale} value={value} onChange={onChange} roundUp={roundUp} position={position} utcOffset={utcOffset} />,
    'data-test-subj': 'superDatePickerAbsoluteTab',
    'aria-label': `${ariaLabel} Absolute`
  }, {
    id: DATE_MODES.RELATIVE,
    name: 'Relative',
    content: <EuiRelativeTab dateFormat={dateFormat} locale={locale} value={toAbsoluteString(value, roundUp)} onChange={onChange} roundUp={roundUp} position={position} />,
    'data-test-subj': 'superDatePickerRelativeTab',
    'aria-label': `${ariaLabel} Relative`
  }, {
    id: DATE_MODES.NOW,
    name: 'Now',
    content: <EuiText size="s" color="subdued" className="euiDatePopoverContent__padded--large">
          <p>
            Setting the time to &quot;now&quot; means that on every refresh this
            time will be set to the time of the refresh.
          </p>
          <EuiButton data-test-subj="superDatePickerNowButton" onClick={() => {
        onChange('now');
      }} fullWidth size="s" fill>
            Set {position} date and time to now
          </EuiButton>
        </EuiText>,
    'data-test-subj': 'superDatePickerNowTab',
    'aria-label': `${ariaLabel} Now`
  }];
  const initialSelectedTab = renderTabs.find(tab => tab.id === getDateMode(value));
  return <EuiTabbedContent className="euiDatePopoverContent" tabs={renderTabs} autoFocus="selected" initialSelectedTab={initialSelectedTab} onTabClick={onTabClick} size="s" expand />;
};
EuiDatePopoverContent.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  roundUp: PropTypes.bool,
  dateFormat: PropTypes.string.isRequired,
  timeFormat: PropTypes.string.isRequired,
  locale: PropTypes.any,
  position: PropTypes.oneOf(["start", "end"]).isRequired,
  utcOffset: PropTypes.number
};
EuiDatePopoverContent.displayName = 'EuiDatePopoverContent';

try {
  // @ts-ignore
  EuiDatePopoverContent.displayName = "EuiDatePopoverContent"; // @ts-ignore

  EuiDatePopoverContent.__docgenInfo = {
    "description": "",
    "displayName": "EuiDatePopoverContent",
    "props": {
      "value": {
        "defaultValue": null,
        "description": "",
        "name": "value",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "onChange": {
        "defaultValue": null,
        "description": "",
        "name": "onChange",
        "required": true,
        "type": {
          "name": "(date: string | null, event?: SyntheticEvent<any, Event> | undefined) => void"
        }
      },
      "roundUp": {
        "defaultValue": {
          value: false
        },
        "description": "",
        "name": "roundUp",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "dateFormat": {
        "defaultValue": null,
        "description": "",
        "name": "dateFormat",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "timeFormat": {
        "defaultValue": null,
        "description": "",
        "name": "timeFormat",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "locale": {
        "defaultValue": null,
        "description": "",
        "name": "locale",
        "required": false,
        "type": {
          "name": "string | boolean | Moment | Duration | string[] | undefined"
        }
      },
      "position": {
        "defaultValue": null,
        "description": "",
        "name": "position",
        "required": true,
        "type": {
          "name": "enum",
          "value": [{
            "value": "\"end\""
          }, {
            "value": "\"start\""
          }]
        }
      },
      "utcOffset": {
        "defaultValue": null,
        "description": "",
        "name": "utcOffset",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/date_picker/super_date_picker/date_popover/date_popover_content.tsx#EuiDatePopoverContent"] = {
      docgenInfo: EuiDatePopoverContent.__docgenInfo,
      name: "EuiDatePopoverContent",
      path: "src/components/date_picker/super_date_picker/date_popover/date_popover_content.tsx#EuiDatePopoverContent"
    };
} catch (__react_docgen_typescript_loader_error) {}